import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { paynimoService } from '../services/paynimoService';

const PaynimoResult = () => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const txnId = searchParams.get('txnId');
  const registrationId = searchParams.get('registrationId');
  const errorParam = searchParams.get('error');
  const statusParam = searchParams.get('status');

  console.log("searchParams +++++++++++++++++++++ ", searchParams);
  
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        // Check if there's an error parameter in URL
        if (errorParam) {
          let errorMessage = 'Payment processing failed';
          switch (errorParam) {
            case 'forwarding_failed':
              errorMessage = 'Failed to process payment callback';
              break;
            case 'hash_validation_failed':
              errorMessage = 'Payment security validation failed';
              break;
            case 'invalid_transaction':
              errorMessage = 'Invalid transaction reference';
              break;
            case 'invalid_response':
              errorMessage = 'Invalid payment response received';
              break;
            case 'processing_failed':
              errorMessage = 'Payment processing failed';
              break;
          }
          throw new Error(errorMessage);
        }

        let transactionId = txnId;
        
        // If no txnId in URL, try to get from sessionStorage
        if (!transactionId) {
          const pendingPayment = sessionStorage.getItem('pendingPaynimoPayment');
          if (pendingPayment) {
            const data = JSON.parse(pendingPayment);
            transactionId = data.txnId;
          }
        }

        if (!transactionId) {
          throw new Error('No transaction ID found');
        }

        const response = await paynimoService.getPaymentStatus(transactionId);
        setPaymentDetails(response.data);

        // Clear sessionStorage after successful fetch
        sessionStorage.removeItem('pendingPaynimoPayment');

        // Show appropriate toast message
        if (response.success) {
          const paymentStatus = response.data.status;
          if (paymentStatus === 'completed') {
            toast.success('Payment completed successfully!');
          } else if (paymentStatus === 'initiated') {
            toast.info('Payment is being processed');
          } else if (paymentStatus === 'failed') {
            toast.error('Payment failed');
          }
        }

      } catch (error) {
        console.error('Error fetching Paynimo payment details:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [txnId]);

  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'initiated':
        return 'warning';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Payment Successful';
      case 'initiated':
        return 'Payment Processing';
      case 'failed':
        return 'Payment Failed';
      default:
        return status || 'Unknown Status';
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleViewRegistrations = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <Card className="shadow">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading payment details...</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <Card className="shadow">
          <Card.Body className="text-center py-5">
            <Alert variant="danger">
              <h5>Error Loading Payment Details</h5>
              <p>{error}</p>
              <Button variant="primary" onClick={handleBackToHome}>
                Back to Home
              </Button>
            </Alert>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <Card className="shadow">
        <Card.Header className="bg-light">
          <h3 className="mb-0">Paynimo Payment Result</h3>
        </Card.Header>
        <Card.Body>
          {paymentDetails && (
            <div>
              <div className="text-center mb-4">
                <Badge 
                  bg={getStatusVariant(paymentDetails.status)} 
                  className="fs-6 p-3"
                >
                  {getStatusText(paymentDetails.status)}
                </Badge>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <h6>Payment Information</h6>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td><strong>Transaction ID:</strong></td>
                        <td>{paymentDetails.transactionId}</td>
                      </tr>
                      <tr>
                        <td><strong>Status:</strong></td>
                        <td>
                          <Badge bg={getStatusVariant(paymentDetails.status)}>
                            {paymentDetails.status}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Registration ID:</strong></td>
                        <td>{paymentDetails.registrationId}</td>
                      </tr>
                      {paymentDetails.paymentMethod && (
                        <tr>
                          <td><strong>Payment Method:</strong></td>
                          <td>{paymentDetails.paymentMethod}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="col-md-6">
                  <h6>Transaction Details</h6>
                  <table className="table table-borderless">
                    <tbody>
                      {paymentDetails.amount && (
                        <tr>
                          <td><strong>Amount:</strong></td>
                          <td>
                            {paymentDetails.currency || 'INR'} {paymentDetails.amount}
                          </td>
                        </tr>
                      )}
                      {paymentDetails.paymentDate && (
                        <tr>
                          <td><strong>Payment Date:</strong></td>
                          <td>{new Date(paymentDetails.paymentDate).toLocaleString()}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {paymentDetails.status === 'completed' ? (
                <Alert variant="success" className="mt-4">
                  <h6>Registration Confirmed!</h6>
                  <p className="mb-0">
                    Your payment has been processed successfully through Paynimo. Your registration for NSDSST 2025 is now confirmed.
                    You will receive a confirmation email shortly.
                  </p>
                </Alert>
              ) : paymentDetails.status === 'initiated' ? (
                <Alert variant="warning" className="mt-4">
                  <h6>Payment Processing</h6>
                  <p className="mb-0">
                    Your payment is being processed through Paynimo. Please allow some time for the transaction to complete.
                    You will be notified once the payment is confirmed.
                  </p>
                </Alert>
              ) : (
                <Alert variant="danger" className="mt-4">
                  <h6>Payment Issue</h6>
                  <p className="mb-0">
                    There was an issue with your payment through Paynimo. Please try again or contact support if the problem persists.
                  </p>
                </Alert>
              )}

              <div className="d-flex justify-content-between mt-4">
                <Button variant="secondary" onClick={handleBackToHome}>
                  Back to Home
                </Button>
                {/* <Button variant="primary" onClick={handleViewRegistrations}>
                  View All Registrations
                </Button> */}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PaynimoResult;
